import React from 'react';
import { DateTime } from 'luxon';
import {
  AdminLegendVariant,
  ICalendarEventType,
  ProfessionalReplacementTypes,
} from '@pharmaplan/common';
import ICalendar from '../../../types/ICalendar';
import { eventsMapper } from '../../Dashboard/MainCalendar/helpers';
import {
  adminBookedLegendObject,
  adminLegendObject,
  adminPostedLegend,
  adminRequestedLegend,
  IAdminLegendItem,
} from './LegendContainer';
import AdminLegend from './AdminLegend';
import AdminSplitLegend from './AdminSplitLegend';
import { AdminSplitLegendEntry } from './AdminSplitLegend/AdminSplitLegend';
import genericClasses from '../../../theme/GenericClasses';

export const getTotalCount = (
  events: Array<ICalendar>,
  filterBy: `${ICalendarEventType}`,
  activeMonth: number,
) =>
  events?.reduce((acc, cur) => {
    const eventActiveMonth = new Date(cur.startDate).getMonth();
    if (cur.type === filterBy && eventActiveMonth === activeMonth) {
      // eslint-disable-next-line no-param-reassign
      acc += 1;
    }
    return acc;
  }, 0);

export const getTotalHrs = (
  events: Array<ICalendar>,
  hoursOf: `${ICalendarEventType}`,
  activeMonth: number,
) =>
  events?.reduce((acc, cur) => {
    const eventActiveMonth = new Date(cur.start).getMonth();
    if (cur.type === hoursOf && eventActiveMonth === activeMonth) {
      // eslint-disable-next-line no-param-reassign
      acc += Math.abs(
        DateTime.fromISO(cur.endDate)
          .diff(DateTime.fromISO(cur.startDate))
          .as('hour'),
      );
    }
    return acc;
  }, 0);

export enum AdminBookedLegendKey {
  bookedPharmacists = 'bookedPharmacists',
  bookedTechnicians = 'bookedTechnicians',
}

export type AdminLegendKey = `${AdminLegendVariant}` | `${AdminBookedLegendKey}`;

const bookedLegendReplacementTypes: Record<AdminBookedLegendKey, ProfessionalReplacementTypes> = {
  [AdminBookedLegendKey.bookedPharmacists]: ProfessionalReplacementTypes.pharmacist,
  [AdminBookedLegendKey.bookedTechnicians]: ProfessionalReplacementTypes.technician,
};

const bookedLegendLabelKeys: Record<AdminBookedLegendKey, AdminSplitLegendEntry['labelKey']> = {
  [AdminBookedLegendKey.bookedPharmacists]: 'pharm',
  [AdminBookedLegendKey.bookedTechnicians]: 'tech',
};

// BOOKED EVENTS ARE SPLIT INTO SEPARATE LEGEND ENTRIES PER PROFESSIONAL TYPE
const getAdminLegendKey = ({ type, replacementType }: ICalendar): AdminLegendKey => {
  if (type !== AdminLegendVariant.booked) {
    return type as AdminLegendKey;
  }

  return replacementType === ProfessionalReplacementTypes.technician
    ? AdminBookedLegendKey.bookedTechnicians
    : AdminBookedLegendKey.bookedPharmacists;
};

const getAdminLegendEvents = (
  events: Array<ICalendar>,
  legendKey: AdminLegendKey,
) =>
  events.filter((event) =>
    getAdminLegendKey(event) === legendKey);

const getAdminLegendCount = (
  events: Array<ICalendar>,
  { key, variant }: IAdminLegendItem,
  activeMonth: number,
) =>
  getTotalCount(getAdminLegendEvents(events, key), variant, activeMonth);

const getAdminLegendHours = (
  events: Array<ICalendar>,
  { key, variant }: IAdminLegendItem,
  activeMonth: number,
) =>
  getTotalHrs(
    getAdminLegendEvents(events, key).map((e) =>
      eventsMapper(e)),
    variant,
    activeMonth,
  );

const sumAdminLegends = (
  legends: Array<IAdminLegendItem>,
  events: Array<ICalendar>,
  activeMonth: number,
) =>
  legends.reduce(
    (acc, curr) => {
      acc.totalHours += getAdminLegendHours(events ?? [], curr, activeMonth);
      acc.totalCount += getAdminLegendCount(events ?? [], curr, activeMonth);
      return acc;
    },
    { totalHours: 0, totalCount: 0 },
  );

export const adminTotal = (events: Array<ICalendar>, activeMonth: number) =>
  sumAdminLegends(adminLegendObject, events, activeMonth);

// BOOKED PHARMACISTS & TECHNICIANS SHARE A SINGLE LEGEND CHIP, EACH OPENING ITS OWN FILTERED REPORT
const getAdminBookedLegendItems = (
  events: ICalendar[],
  currentActiveMonth: number,
): Array<AdminSplitLegendEntry> =>
  adminBookedLegendObject.map((leg) => {
    const bookedKey = leg.key as AdminBookedLegendKey;

    return {
      labelKey: bookedLegendLabelKeys[bookedKey],
      replacementType: bookedLegendReplacementTypes[bookedKey],
      totalCount: getAdminLegendCount(events, leg, currentActiveMonth),
      totalHrs: getAdminLegendHours(events, leg, currentActiveMonth),
    };
  });

// BOOKED TOTAL & GRAND TOTAL SHARE A SINGLE NON-CLICKABLE LEGEND CHIP
const getAdminTotalLegendItems = (
  events: ICalendar[],
  currentActiveMonth: number,
  grandTotalCount: number,
  grandTotalHours: number,
): Array<AdminSplitLegendEntry> => {
  const bookedTotal = sumAdminLegends(adminBookedLegendObject, events, currentActiveMonth);

  return [
    {
      labelKey: 'booked',
      replacementType: null,
      totalCount: bookedTotal.totalCount,
      totalHrs: bookedTotal.totalHours,
    },
    {
      labelKey: 'grand',
      replacementType: null,
      totalCount: grandTotalCount,
      totalHrs: grandTotalHours,
    },
  ];
};

export const AdminRenderer = (
  totalCount: number,
  totalHours: number,
  events: ICalendar[],
  currentActiveMonth: number,
  row:boolean,
) => {
  const customClass = row ? genericClasses.legendsRowMargin : genericClasses.legendsMargin;

  const renderAdminLegend = (leg: IAdminLegendItem) =>
    (
      <AdminLegend
        totalCount={getAdminLegendCount(events, leg, currentActiveMonth)}
        totalHrs={Math.trunc(getAdminLegendHours(events, leg, currentActiveMonth))}
        variant={leg.variant}
        isRow={row}
        customClass={customClass}
        key={leg.key}
      />
    );

  return [
    renderAdminLegend(adminRequestedLegend),
    renderAdminLegend(adminPostedLegend),
    <AdminSplitLegend
      key={AdminLegendVariant.booked}
      variant={AdminLegendVariant.booked}
      items={getAdminBookedLegendItems(events, currentActiveMonth)}
      isRow={row}
      customClass={customClass}
    />,
    <AdminSplitLegend
      key={AdminLegendVariant.total}
      variant={AdminLegendVariant.total}
      items={getAdminTotalLegendItems(events, currentActiveMonth, totalCount, totalHours)}
      isRow={row}
      customClass={customClass}
    />,
  ];
};
